import React, { useState } from 'react';
import { generateTeleologyShift } from '../services/geminiService';
import { TeleologyShift } from '../types';
import { Compass, RefreshCw, Mountain, ArrowRight } from 'lucide-react';
import { useUser } from '../context/UserContext';

const CourageShift: React.FC = () => {
  const [fear, setFear] = useState('');
  const [shift, setShift] = useState<TeleologyShift | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleShift = async () => {
    if (!fear.trim()) return;
    setLoading(true);
    setShift(null);
    try {
      const data = await generateTeleologyShift(fear);
      setShift(data);
      addXp(50, 'Shifted to Teleology');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-cyan-50 text-cyan-600 rounded-full mb-4">
          <Compass size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Freedom Shift</h1>
        <p className="text-nunchi-600">
          Stop asking "Why is this happening?" (Etiology). <br/>
          Start asking "What is the purpose of my reaction?" (Teleology).
        </p>
      </header>

      {!shift && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                Identify a limitation or fear ("I can't X because Y")
            </label>
            <textarea
              value={fear}
              onChange={(e) => setFear(e.target.value)}
              placeholder="e.g., I can't socialize because I'm shy, I can't start a business because I might fail..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-cyan-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleShift}
              disabled={!fear.trim()}
              className="w-full bg-cyan-700 text-white py-4 rounded-xl font-bold hover:bg-cyan-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Compass size={18} /> Find The Purpose
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-cyan-100 border-t-cyan-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-cyan-700">Looking for the hidden goal...</p>
        </div>
      )}

      {shift && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-cyan-900 text-cyan-50 p-8 rounded-2xl shadow-xl relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <div className="mb-8">
                     <span className="inline-block px-3 py-1 bg-cyan-800 text-cyan-200 text-xs font-bold uppercase tracking-widest rounded-full mb-3">
                         Hidden Goal
                     </span>
                     <h2 className="text-2xl font-serif font-bold text-white mb-2">"{shift.hiddenGoal}"</h2>
                     <p className="text-cyan-200 text-sm">You are creating this emotion to achieve this goal.</p>
                 </div>

                 <div className="mb-6 border-l-4 border-cyan-500 pl-4">
                     <h3 className="text-cyan-300 uppercase tracking-widest text-xs font-bold mb-2">The Reframe</h3>
                     <p className="text-lg leading-relaxed font-serif text-white">
                         {shift.reframe}
                     </p>
                 </div>

                 <div className="bg-white/10 p-4 rounded-xl flex items-start gap-4">
                     <Mountain className="flex-shrink-0 text-cyan-300 mt-1" size={24} />
                     <div>
                         <h3 className="text-cyan-300 uppercase tracking-widest text-xs font-bold mb-1">Courageous Action</h3>
                         <p className="font-bold text-white">{shift.action}</p>
                     </div>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setShift(null); setFear(''); }}
                  className="text-cyan-600 hover:text-cyan-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Shift Another Fear
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default CourageShift;